// src/components/signage/SignageHealthCard.jsx
//
// Live operational status card for the Cartelería admin UI. Polls
// GET /apitest/signage/health every 30 s, renders per-device status badges,
// and offers a "Refrescar sesión" button per row that posts to
// POST /apitest/api/devices/:deviceId/heartbeat.
//
// Polling stops when the page is hidden and resumes on visibility return so
// we don't burn a request slot when nobody is watching.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Button, Skeleton, Tooltip } from 'antd';
import {
    CheckCircleTwoTone,
    WarningTwoTone,
    CloseCircleTwoTone,
    QuestionCircleTwoTone,
    ReloadOutlined,
} from '@ant-design/icons';
import { getSignageHealth } from '../../services/signageService';
import { heartbeatDevice } from '../../services/deviceService';
import './SignageHealthCard.css';

const POLL_INTERVAL_MS = 30_000;

const STATUS_META = {
    ONLINE:    { color: 'success', icon: CheckCircleTwoTone,   twoToneColor: '#52c41a', label: 'En línea' },
    STALE:     { color: 'warning', icon: WarningTwoTone,      twoToneColor: '#faad14', label: 'Sin actividad reciente' },
    REVOKED:   { color: 'error',   icon: CloseCircleTwoTone,   twoToneColor: '#ff4d4f', label: 'Revocado' },
    EXPIRED:   { color: 'error',   icon: CloseCircleTwoTone,   twoToneColor: '#ff4d4f', label: 'Expirado' },
    UNKNOWN:   { color: 'default', icon: QuestionCircleTwoTone, twoToneColor: '#999', label: 'Desconocido' },
};

function formatAgo(seconds) {
    if (seconds == null) return '—';
    if (seconds < 60) return `hace ${seconds}s`;
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
    if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)}h`;
    return `hace ${Math.floor(seconds / 86_400)}d`;
}

function formatIssuedBy(user) {
    if (!user || !user.userId) return 'desconocido';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || `User #${user.userId}`;
}

export default function SignageHealthCard({ authUser }) {
    const [health, setHealth]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [busyId, setBusyId]   = useState(null);
    const [toast, setToast]     = useState(null);
    const [expanded, setExpanded] = useState(false);
    const pollRef = useRef(null);

    const canHeartbeat = !!authUser && authUser.role === 'ClientAdministrator';

    // REVOKED/EXPIRED credentials never come back online on their own
    // (EXPIRED just needs re-issue; REVOKED is permanently removed). Hide them
    // entirely from the Cartelería card so the panel only reflects the
    // ground-truth live state of the kiosks.
    const VISIBLE_STATUSES = new Set(['ONLINE', 'STALE', 'EXPIRED']);
    const visibleDevices   = health?.devices ? health.devices.filter((d) => VISIBLE_STATUSES.has(d.status)) : [];
    const revokedCount    = health?.devices ? health.devices.length - visibleDevices.length : 0;
    const onlineCount = visibleDevices.filter((d) => d.status === 'ONLINE').length;
    const staleCount  = visibleDevices.filter((d) => d.status === 'STALE').length;
    const expiredCount = visibleDevices.filter((d) => d.status === 'EXPIRED').length;

    const fetchHealth = useCallback(async () => {
        try {
            const data = await getSignageHealth();
            setHealth(data);
            setError(null);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403 || status === 401) {
                setError('No tiene permisos para ver el estado de cartelería.');
            } else if (status === 404) {
                setError('Signage no habilitado para esta organización.');
            } else {
                setError('No se pudo obtener el estado de cartelería.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHealth();
        function startPolling() {
            if (pollRef.current) return;
            pollRef.current = setInterval(fetchHealth, POLL_INTERVAL_MS);
        }
        function stopPolling() {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
        }
        function onVisibility() {
            if (document.hidden) stopPolling();
            else { fetchHealth(); startPolling(); }
        }
        startPolling();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fetchHealth]);

    const onHeartbeat = useCallback(async (device) => {
        if (busyId) return;
        setBusyId(device.deviceId);
        try {
            await heartbeatDevice(device.deviceId);
            setToast({
                kind: 'success',
                message: `Sesión refrescada para "${device.label}" — expira en 30 días.`,
            });
            fetchHealth();
        } catch (e) {
            const code = e?.response?.data?.code || 'ERROR';
            const msg  = e?.response?.data?.error || e?.message || 'Error desconocido';
            setToast({ kind: 'error', message: `No se pudo refrescar (${code}): ${msg}` });
        } finally {
            setBusyId(null);
        }
    }, [busyId, fetchHealth]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    if (loading) {
        return (
            <div className="signage-health-card">
                <Skeleton active paragraph={{ rows: 2 }} />
            </div>
        );
    }
    if (error) {
        return (
            <div className="signage-health-card signage-health-card--error">
                <span>{error}</span>
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchHealth}>Reintentar</Button>
            </div>
        );
    }
    if (!health) return null;

    const { organization, activeCampaign, devices, generatedAt, healthy } = health;

    return (
        <div className={`signage-health-card ${healthy ? 'is-healthy' : 'is-degraded'}`}>
            <div className="signage-health-card__header">
                <div className="signage-health-card__title">
                    <h3>Estado de Cartelería</h3>
                    <span className="signage-health-card__org">
                        {organization.organizationName || `#${organization.organizationId}`}
                        {' · '}
                        <Badge
                            status={healthy ? 'success' : 'warning'}
                            text={healthy ? 'Operativo' : 'Con observaciones'}
                        />
                    </span>
                </div>
                <div className="signage-health-card__meta">
                    <span className="signage-health-card__timestamp">
                        Actualizado {new Date(generatedAt).toLocaleTimeString()}
                    </span>
                    <Tooltip title="Refrescar">
                        <Button
                            size="small"
                            type="text"
                            icon={<ReloadOutlined />}
                            onClick={fetchHealth}
                        />
                    </Tooltip>
                </div>
            </div>

            <div className="signage-health-card__summary">
                <div className="signage-health-card__summary-row">
                    <span className="label">Signage habilitado</span>
                    <Badge
                        status={organization.signageEnabled ? 'success' : 'default'}
                        text={organization.signageEnabled ? 'Sí' : 'No'}
                    />
                </div>
                <div className="signage-health-card__summary-row">
                    <span className="label">Campaña activa</span>
                    {activeCampaign ? (
                        <span>
                            <strong>{activeCampaign.name}</strong>
                            {' · '}
                            {activeCampaign.slideCount} diapositivas
                        </span>
                    ) : (
                        <Badge status="warning" text="Sin campaña activa" />
                    )}
                </div>
            </div>

            <div className="signage-health-card__devices">
                <div className="signage-health-card__devices-summary" role="button" onClick={() => setExpanded((v) => !v)}>
                    <div className="summary-chips">
                        <Badge status="processing" count={visibleDevices.length} showZero={false} />
                        <span>Dispositivos</span>
                        {onlineCount > 0 && <Badge color="green" text={`${onlineCount} en línea`} />}
                        {staleCount > 0  && <Badge color="gold"  text={`${staleCount} sin actividad reciente`} />}
                        {expiredCount > 0 && <Badge color="red" text={`${expiredCount} expirados`} />}
                    </div>
                    <Button size="small" type="text">
                        {expanded ? 'Ocultar' : 'Ver detalle'}
                    </Button>
                </div>

                {expanded && (
                    <>
                        {visibleDevices.length === 0 ? (
                            <div className="signage-health-card__empty">
                                No hay dispositivos registrados.
                            </div>
                        ) : (
                            visibleDevices.map((d) => {
                                const meta = STATUS_META[d.status] || STATUS_META.UNKNOWN;
                                const StatusIcon = meta.icon;
                                const canRefresh = canHeartbeat && d.status !== 'EXPIRED' && d.status !== 'REVOKED';
                                return (
                                    <div className="signage-health-card__device-row" key={d.deviceId}>
                                        <div className="device-cell">
                                            <strong>{d.label}</strong>
                                            <small>
                                                …{d.tokenLastFour}
                                                {' · emitido por '}
                                                {formatIssuedBy(d.issuedBy)}
                                            </small>
                                        </div>
                                        <div className="status-cell">
                                            <StatusIcon twoToneColor={meta.twoToneColor} />
                                            <span>{meta.label}</span>
                                        </div>
                                        <div className="lastseen-cell">
                                            <div>{formatAgo(d.lastSeen.secondsAgo)}</div>
                                            {d.lastSeen.ip && d.lastSeen.ip !== 'unknown' && (
                                                <small>{d.lastSeen.ip}</small>
                                            )}
                                        </div>
                                        <div className="expires-cell">
                                            {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString() : '—'}
                                        </div>
                                        <div className="action-cell">
                                            {canHeartbeat && (
                                                <Tooltip
                                                    title={
                                                        d.status === 'EXPIRED'
                                                            ? 'La credencial subyacente expiró; emitir una nueva desde la herramienta de administración.'
                                                            : 'Forzar un JWT fresco de 30 días y marcar al kiosko como activo.'
                                                    }
                                                >
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        icon={<ReloadOutlined />}
                                                        loading={busyId === d.deviceId}
                                                        disabled={!canRefresh}
                                                        onClick={() => onHeartbeat(d)}
                                                    >
                                                        Refrescar sesión
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </>
                )}
            </div>

            {toast && (
                <div className={`signage-health-card__toast toast--${toast.kind}`}>
                    <span>{toast.message}</span>
                    <Button size="small" type="text" onClick={() => setToast(null)}>×</Button>
                </div>
            )}
        </div>
    );
}
